import { useState, useEffect } from "react";
import { getFornitori, createFornitore, deleteFornitore, updateFornitore, bulkDeleteFornitori } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Truck, Search, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function ArchivioFornitori() {
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFornitore, setEditingFornitore] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    contatto: "",
    indirizzo: "",
  });

  useEffect(() => {
    loadFornitori();
  }, []);

  const loadFornitori = async () => {
    try {
      const response = await getFornitori();
      setFornitori(response.data);
      setSelectedIds([]);
    } catch (error) {
      console.error("Error loading fornitori:", error);
      toast.error("Errore nel caricamento dei fornitori");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }

    try {
      if (editingFornitore) {
        await updateFornitore(editingFornitore.id, formData);
        toast.success("Fornitore aggiornato con successo");
      } else {
        await createFornitore(formData);
        toast.success("Fornitore creato con successo");
      }
      
      setDialogOpen(false);
      setEditingFornitore(null);
      setFormData({ nome: "", contatto: "", indirizzo: "" });
      loadFornitori();
    } catch (error) {
      console.error("Error saving fornitore:", error);
      toast.error("Errore nel salvataggio del fornitore");
    }
  };

  const handleEdit = (fornitore) => {
    setEditingFornitore(fornitore);
    setFormData({
      nome: fornitore.nome,
      contatto: fornitore.contatto || "",
      indirizzo: fornitore.indirizzo || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteFornitore(id);
      toast.success("Fornitore eliminato con successo");
      setDeleteConfirm(null);
      loadFornitori();
    } catch (error) {
      console.error("Error deleting fornitore:", error);
      toast.error("Errore nell'eliminazione del fornitore");
    }
  };

  const handleOpenDialog = () => {
    setEditingFornitore(null);
    setFormData({ nome: "", contatto: "", indirizzo: "" });
    setDialogOpen(true);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredFornitori.map((f) => f.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteFornitori(selectedIds);
      toast.success(`${selectedIds.length} fornitori eliminati con successo`);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      loadFornitori();
    } catch (error) {
      console.error("Error bulk deleting fornitori:", error);
      toast.error("Errore nell'eliminazione multipla");
    }
  };

  const filteredFornitori = fornitori.filter((f) =>
    f.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (f.contatto && f.contatto.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="archivio-fornitori-page" className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
            Archivio Fornitori
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Gestisci l'anagrafica dei fornitori
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="nuovo-fornitore-btn"
              className="btn-accent rounded-lg"
              onClick={handleOpenDialog}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Fornitore
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
                <Truck className="w-5 h-5 text-orange-500" />
                {editingFornitore ? "Modifica Fornitore" : "Nuovo Fornitore"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="nome" className="font-heading font-bold text-slate-700">
                  Nome *
                </Label>
                <Input
                  id="nome"
                  data-testid="fornitore-nome-input"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Es: Ditta Rossi S.r.l."
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contatto" className="font-heading font-bold text-slate-700">
                  Contatto
                </Label>
                <Input
                  id="contatto"
                  data-testid="fornitore-contatto-input"
                  value={formData.contatto}
                  onChange={(e) => setFormData({ ...formData, contatto: e.target.value })}
                  placeholder="Es: 02 1234567 / info@ditta.it"
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="indirizzo" className="font-heading font-bold text-slate-700">
                  Indirizzo
                </Label>
                <Input
                  id="indirizzo"
                  data-testid="fornitore-indirizzo-input"
                  value={formData.indirizzo}
                  onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                  placeholder="Es: Via Roma 1, Milano"
                  className="rounded-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-sm"
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  data-testid="submit-fornitore-btn"
                  className="btn-primary rounded-sm"
                >
                  {editingFornitore ? "Salva Modifiche" : "Crea Fornitore"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="card-industrial">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="search-fornitori-input"
              placeholder="Cerca per nome o contatto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="card-industrial">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" />
              Elenco Fornitori ({filteredFornitori.length})
            </CardTitle>
            {selectedIds.length > 0 && (
              <Button
                data-testid="bulk-delete-fornitori-btn"
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteConfirm(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina Selezionati ({selectedIds.length})
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {filteredFornitori.length > 0 ? (
              <div className="table-scroll-wrapper">
                <div className="rounded-sm border border-slate-200 overflow-hidden">
                  <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-12">
                        <Checkbox
                          data-testid="select-all-fornitori"
                          checked={filteredFornitori.length > 0 && selectedIds.length === filteredFornitori.length}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Nome
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Contatto
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Indirizzo
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Data Creazione
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-center">
                        Azioni
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFornitori.map((fornitore, index) => (
                      <TableRow 
                        key={fornitore.id}
                        data-testid={`fornitore-row-${index}`}
                        className={`hover:bg-slate-50/50 ${selectedIds.includes(fornitore.id) ? "bg-orange-50" : ""}`}
                      >
                        <TableCell>
                          <Checkbox
                            data-testid={`select-fornitore-${index}`}
                            checked={selectedIds.includes(fornitore.id)}
                            onCheckedChange={(checked) => handleSelectItem(fornitore.id, checked)}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          {fornitore.nome}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {fornitore.contatto || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {fornitore.indirizzo || "-"}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-500">
                          {formatDate(fornitore.created_at)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`edit-fornitore-${index}`}
                              onClick={() => handleEdit(fornitore)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-fornitore-${index}`}
                              onClick={() => setDeleteConfirm(fornitore)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Truck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">
                  {searchTerm ? "Nessun fornitore trovato" : "Nessun fornitore registrato"}
                </p>
                <p className="text-sm mt-1">
                  {searchTerm ? "Prova con una ricerca diversa" : "Clicca su 'Nuovo Fornitore' per iniziare"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il fornitore <span className="font-bold">{deleteConfirm?.nome}</span>?
              <br />
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-fornitore-btn"
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-600">Eliminazione Multipla</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare <span className="font-bold">{selectedIds.length} fornitori</span>?
              <br />
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-bulk-delete-fornitori-btn"
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina {selectedIds.length} Fornitori
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
